import React, { useEffect, useState } from 'react';
import { Card, CardBody, Row, Col, Badge, FormGroup, Input, Label, Spinner, Alert } from 'reactstrap';
import moment from 'moment';
import api from '../../services/api';
import styles from '../../styles/public/league-registration.module.css';

const LeagueScheduleSelector = ({
    leagueGuid,
    schedules,
    selectedSchedule,
    onSelectSchedule,
    isOtherScheduleSelected,
    onSelectOtherSchedule,
    selectedScoringSchedule,
    onSelectScoringSchedule,
    selectedTimeslot,
    onSelectAvailability
}) => {
    const [availableTimeslots, setAvailableTimeslots] = useState([]);
    const [loadingTimeslots, setLoadingTimeslots] = useState(false);
    const [timeslotsError, setTimeslotsError] = useState(null);

    // Preferred day is determined by the scoring schedule's own day, not chosen separately
    const scheduleDay = (selectedScoringSchedule?.day_of_week !== null && selectedScoringSchedule?.day_of_week !== undefined)
        ? getDayName(selectedScoringSchedule.day_of_week)
        : null;

    useEffect(() => {
        if (!leagueGuid || !scheduleDay) {
            setAvailableTimeslots([]);
            return;
        }

        const fetchAvailableTimeslots = async () => {
            setLoadingTimeslots(true);
            setTimeslotsError(null);

            try {
                const response = await api.get(`/leagues/${leagueGuid}/available-timeslots/`, {
                    params: { day: scheduleDay },
                    skipAuthRedirect: true
                });
                setAvailableTimeslots(Array.isArray(response.data) ? response.data : response.data.timeslots || []);
            } catch (error) {
                console.error('Error fetching available league timeslots:', error);
                setAvailableTimeslots([]);
                setTimeslotsError('Unable to load available times for this day.');
            } finally {
                setLoadingTimeslots(false);
            }
        };

        fetchAvailableTimeslots();
    }, [leagueGuid, scheduleDay]);

    const handleScoringScheduleChange = (event) => {
        event.stopPropagation();
        onSelectOtherSchedule();
        const schedule = schedules.find((s) => String(s.id) === event.target.value) || null;
        onSelectScoringSchedule(schedule);
        const newDay = (schedule?.day_of_week !== null && schedule?.day_of_week !== undefined)
            ? getDayName(schedule.day_of_week)
            : '';
        onSelectAvailability({ day: newDay, timeslot: null });
    };

    const getTimeslotValue = (timeslot) => String(
        timeslot?.id ?? timeslot?.guid ?? timeslot?.slot ?? timeslot?.start ?? ''
    );

    const handleTimeslotChange = (event) => {
        event.stopPropagation();
        const timeslot = availableTimeslots.find((option) => getTimeslotValue(option) === event.target.value) || null;
        onSelectAvailability({ day: scheduleDay, timeslot });
    };

    return (
        <>
            {!schedules || schedules.length === 0 ? (
                <div className="text-center py-4">
                    <p className="text-muted">No schedules are currently available for this league.</p>
                </div>
            ) : (
                <Row className="g-3">
                {schedules.map((schedule) => {
                const isSelected = selectedSchedule?.id === schedule.id;
                const isFull = schedule.is_full;
                
                return (
                    <Col xs={12} md={6} key={schedule.id}>
                        <Card 
                            className={`${styles.scheduleCard} ${isSelected ? styles.selected : ''} ${isFull ? styles.disabled : ''}`}
                            onClick={() => !isFull && onSelectSchedule(schedule)}
                            style={{ cursor: isFull ? 'not-allowed' : 'pointer', opacity: isFull ? 0.6 : 1 }}
                        >
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h5 className="mb-0">{schedule.title}</h5>
                                    <div className="d-flex gap-2">
                                        {isFull && (
                                            <Badge color="danger">
                                                <i className="fas fa-ban me-1"></i> Full
                                            </Badge>
                                        )}
                                        {isSelected && (
                                            <Badge color="success">
                                                <i className="fas fa-check me-1"></i> Selected
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                
                                {schedule.description && (
                                    <p className="text-muted small mb-3">
                                        {schedule.description}
                                    </p>
                                )}

                                <div className={styles.scheduleDetails}>                                    
                                    {schedule.start_time && (
                                        <div className="mb-2">
                                            <i className="fas fa-clock me-2 text-primary"></i>
                                            <strong>Time:</strong> {formatTime(schedule.start_time)}
                                            {schedule.end_time && ` - ${formatTime(schedule.end_time)}`}
                                        </div>
                                    )}

                                    {schedule.start_date && (
                                        <div className="mb-2">
                                            <i className="fas fa-calendar-alt me-2 text-primary"></i>
                                            <strong>Starts:</strong> {moment(schedule.start_date).format('MMM D, YYYY')}
                                        </div>
                                    )}

                                    {schedule.duration_minutes && (
                                        <div className="mb-2">
                                            <i className="fas fa-hourglass-half me-2 text-primary"></i>
                                            <strong>Duration:</strong> {schedule.duration_minutes} minutes
                                        </div>
                                    )}

                                    {isFull ? (
                                        <div className="mt-3 pt-2 border-top">
                                            <p className="text-muted small mb-0">
                                                Use the Other Schedule option below for a time outside of the normal league window for this day.
                                            </p>
                                        </div>
                                    ) : schedule.timeslots && schedule.timeslots.length > 0 && (
                                        <div className="mt-3 pt-2 border-top">
                                            <div className="ps-4">
                                                {schedule.timeslots.map((timeslot, index) => (
                                                    <div key={timeslot.id} className="small text-muted mb-1">
                                                        <i className="fas fa-circle me-2" style={{ fontSize: '0.4rem' }}></i>
                                                        {timeslot.start} - {timeslot.end}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                );
                })}
                </Row>
            )}
            <Card
                className={`mt-4 ${styles.scheduleCard} ${isOtherScheduleSelected ? styles.selected : ''}`}
                onClick={onSelectOtherSchedule}
                style={{ cursor: 'pointer' }}
            >
                <CardBody>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <h5 className="mb-0">Other Schedule</h5>
                        {isOtherScheduleSelected && (
                            <Badge color="success">
                                <i className="fas fa-check me-1"></i> Selected
                            </Badge>
                        )}
                    </div>
                    <Row className="g-3">
                        <Col xs={12} md={6}>
                            <FormGroup className="mb-0">
                                <Label for="league-scoring-schedule">Schedule for scoring</Label>
                                <Input
                                    id="league-scoring-schedule"
                                    type="select"
                                    value={selectedScoringSchedule?.id || ''}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={handleScoringScheduleChange}
                                >
                                    <option value="">Select a schedule</option>
                                    {(schedules || []).map((schedule) => (
                                        <option key={schedule.id} value={schedule.id}>
                                            {schedule.title}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col xs={12} md={6}>
                            <FormGroup className="mb-0">
                                <Label for="league-availability-time">Preferred time{scheduleDay ? ` (${scheduleDay})` : ''}</Label>
                                <Input
                                    id="league-availability-time"
                                    type="select"
                                    value={getTimeslotValue(selectedTimeslot)}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={handleTimeslotChange}
                                    disabled={!isOtherScheduleSelected || !scheduleDay || loadingTimeslots || availableTimeslots.length === 0}
                                >
                                    <option value="">
                                        {loadingTimeslots ? 'Loading times...' : 'Select a time'}
                                    </option>
                                    {availableTimeslots.map((timeslot) => (
                                        <option key={getTimeslotValue(timeslot)} value={getTimeslotValue(timeslot)}>
                                            {formatTime(timeslot.start)}{timeslot.end ? ` - ${formatTime(timeslot.end)}` : ''}
                                        </option>
                                    ))}
                                </Input>
                                {loadingTimeslots && <Spinner size="sm" color="primary" className="mt-2" />}
                            </FormGroup>
                        </Col>
                    </Row>
                    {timeslotsError && <Alert color="danger" className="mt-3 mb-0">{timeslotsError}</Alert>}
                    {scheduleDay && !loadingTimeslots && !timeslotsError && availableTimeslots.length === 0 && (
                        <p className="text-muted small mt-3 mb-0">No times are currently available for {scheduleDay}.</p>
                    )}
                </CardBody>
            </Card>
        </>
    );
};

// Helper function to get day name from number (0 = Monday)
const getDayName = (dayNum) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayNum] || 'N/A';
};

// Helper function to format time (HH:MM:SS to h:mm A)
const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return moment(date).format('h:mm A');
};

export default LeagueScheduleSelector;
